import { canSeeFinance } from '../finance/financeTabs';
import { canManageSettings } from '../settings/access';
export type Permission = { resource: string; action: string; scope: 'all' | 'own' };

/**
 * Response of GET /api/auth/me. `hiddenFields`: per resource (every one, nested lists too), the fields the server
 * cuts from its responses (rule 7a: such fields are neither shown nor sent).
 */
/** `seesAllEvents`: the account's own „Domyślnie widzi wszystkie wydarzenia” (spec 2026-10-23-widocznosc-wydarzen). */
export type Me = { id: string; name: string; email: string; isOwner: boolean; seesAllEvents?: boolean; permissions: Permission[]; hiddenFields?: Record<string, string[]> };

/**
 * Rentals of every event (statistics of a supplier's positions, Finanse → Dopożyczenia): the rentals in scope
 * „wszystkie” and every event seen — the owner, or „Domyślnie widzi wszystkie wydarzenia” (review I7: else 403).
 */
export const seesRentalsOfEveryEvent = (me: Me): boolean =>
  (me.isOwner || me.seesAllEvents === true) && me.permissions.some((p) => p.resource === 'subrentals.rentals' && p.action === 'view' && p.scope === 'all');

/** Whether the server sends (and accepts) `field` of `resource` for this user. */
export function fieldVisible(me: Me, resource: string, field: string): boolean {
  return me.isOwner || !(me.hiddenFields?.[resource] ?? []).includes(field);
}

/**
 * Whether this user may write `field` of `resource`: the server refuses writing exactly the fields it hides
 * (`assertMayWriteFields`: "writing a field you may not see would change it blind"), so the same rule. The action's
 * right (e.g. "Kosztorysy: Edycja") is checked apart, with `can`.
 */
export function fieldWritable(me: Me, resource: string, field: string): boolean {
  return fieldVisible(me, resource, field);
}

export function can(me: Me, resource: string, action: string): boolean {
  return me.isOwner || me.permissions.some((p) => p.resource === resource && p.action === action);
}

/** "Nowe wydarzenie": the wizard creates an event, so it needs both rights (menu, route and the button agree). */
export const canPlan = (me: Me): boolean => can(me, 'planning.cards', 'create') && can(me, 'events.events', 'create');

export type MenuItem = {
  to: string;
  label: string;
  /** The label with what the menu knows (e.g. „Mój kalendarz (2)”); absent — `label`. */
  labelWith?: (ctx: MenuContext) => string;
  /** Backend feature id (hints hook onto it). */
  featureId: string;
  visible: (me: Me, ctx: MenuContext) => boolean;
};

/** "Mój kalendarz" by permissions; an account linked with a person (it has dates and tasks) sees it anyway. */
export const canSeeMyCalendar = (me: Me): boolean =>
  can(me, 'events.events', 'view') || can(me, 'crew.assignments', 'view') || can(me, 'schedule.tasks', 'view') || can(me, 'schedule.stages', 'view');

/** What the menu knows besides permissions: `assignedTasks` — the user has an open company task given to them (P7);
 * `linkedPerson` — the account is linked with a person in Osoby (F19: own dates and "Do zrobienia"). */
export type MenuContext = { assignedTasks?: boolean; linkedPerson?: boolean; myQuestions?: number };

const MENU: MenuItem[] = [
  { to: '/uzytkownicy', label: 'Użytkownicy', featureId: 'identity.users.manage', visible: (me) => can(me, 'identity.users', 'view') },
  { to: '/kontrahenci', label: 'Kontrahenci', featureId: 'contractors.list', visible: (me) => can(me, 'contractors.contractors', 'view') },
  {
    to: '/osoby',
    label: 'Osoby',
    featureId: 'people.list',
    visible: (me) => can(me, 'people.persons', 'view') || can(me, 'people.functions', 'view'),
  },
  // "Podsumowanie" (owner 2026-10-24 01:46: „zakladke na wierzchu”): everybody's — each section shows only what the user may see
  { to: '/podsumowanie', label: 'Podsumowanie', featureId: 'todo.summary', visible: () => true },
  { to: '/wydarzenia', label: 'Wydarzenia', featureId: 'events.list', visible: (me) => can(me, 'events.events', 'view') },
  // Own dates of the crew and "Do zrobienia": whoever may see events, crews or tasks (the page itself needs only a session).
  // owner 2026-10-28 21:32: „Mój kalendarz (N)” — N questions waiting for my answer
  {
    to: '/moj-kalendarz',
    label: 'Mój kalendarz',
    labelWith: (ctx) => (ctx.myQuestions ? `Mój kalendarz (${ctx.myQuestions})` : 'Mój kalendarz'),
    featureId: 'crew.my_calendar',
    visible: (me, ctx) => canSeeMyCalendar(me) || !!ctx.linkedPerson,
  },
  {
    // owner 20.10 23:37: "nie nowa impreza tylko nowe wydarzenie" (the address stays)
    to: '/nowa-impreza',
    label: 'Nowe wydarzenie',
    featureId: 'planning.wizard',
    visible: canPlan,
  },
  // Company tasks (P7) and "Moje zadania" (zadania prywatne, 2026-10-23): everybody has their own private tasks, so
  // the entry is everybody's; the company tab inside shows only to whoever may see company tasks (or has one given)
  { to: '/zadania', label: 'Zadania', featureId: 'company-tasks.list', visible: () => true },
  // "Wyceny" (owner remark 25): quotes done and to do; P16 (owner 23:32): only for whoever may see quotes (own or all)
  { to: '/wyceny', label: 'Wyceny', featureId: 'quotes.list', visible: (me) => can(me, 'quotes.quotes', 'view') },
  { to: '/katalog', label: 'Katalog sprzętu', featureId: 'catalog.list', visible: (me) => can(me, 'catalog.items', 'view') },
  {
    // owner 20.10 23:37: "Szablony" with templates, blocks and kits as its tabs
    to: '/szablony',
    label: 'Szablony',
    featureId: 'templates.templates',
    visible: (me) => can(me, 'templates.templates', 'view') || can(me, 'templates.blocks', 'view') || can(me, 'kits.kits', 'view'),
  },
  { to: '/reguly', label: 'Reguły', featureId: 'rules.edit', visible: (me) => can(me, 'rules.rules', 'view') },
  { to: '/dostepnosc', label: 'Dostępność', featureId: 'availability.check', visible: (me) => can(me, 'availability.reservations', 'view') || can(me, 'crew.people_availability', 'view') },
  // F32: one entry with tabs inside (also "Cenniki"), shown to whoever sees any of the tabs (by default the owner only)
  { to: '/finanse', label: 'Finanse', featureId: 'finance.tabs', visible: (me) => canSeeFinance(me) },
  { to: '/powiadomienia/wysylka', label: 'Wysyłka powiadomień', featureId: 'notifications.failed', visible: (me) => can(me, 'notifications.failed', 'view') },
  { to: '/tresc-podpowiedzi', label: 'Treść podpowiedzi', featureId: 'hints.edit', visible: (me) => can(me, 'hints.content', 'view') },
  // Lists and preferences of the company (systems, warehouse, checklists): whoever may change any of them.
  { to: '/ustawienia', label: 'Ustawienia firmy', featureId: 'events.settings', visible: (me) => canManageSettings(me).any },
  // The permission editor is owner-only on the server too.
  { to: '/uprawnienia', label: 'Uprawnienia', featureId: 'identity.roles.permissions', visible: (me) => me.isOwner },
];

/** Menu entries the user may open. Sections that do not exist yet are not listed at all. */
export function visibleMenu(me: Me, ctx: MenuContext = {}): MenuItem[] {
  return MENU.filter((item) => item.visible(me, ctx)).map(({ labelWith, ...item }) => (labelWith ? { ...item, label: labelWith(ctx) } : item));
}
